import { Box, Title, Group, Select, ActionIcon, Button, Paper, Text, Flex, Stack, Alert } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconGripVertical, IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';
import { SectionProps, DimensionItem, FieldType } from '../../types';
import classes from './Dimensions.module.css';
import { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

type SortableDimensionItemProps = {
  id: string;
  dimension: DimensionItem;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onUpdate: ( index: number, dimension: DimensionItem ) => void;
  getAvailableFields: () => FieldType[];
  isRemoveDisabled: boolean;
};

function SortableDimensionItem({ id, dimension, index, isEditing, onEdit, onRemove, onUpdate, getAvailableFields, isRemoveDisabled }: SortableDimensionItemProps ) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const [ editingValues, setEditingValues ] = useState<DimensionItem>( dimension );
  const { t } = useTranslation( 'widgets' );

  const style = {
    transform: CSS.Transform.toString( transform ),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const memberName = dimension.field.split( '.' )[1] || dimension.field;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      p="sm"
      withBorder={false}
      mb="xs"
      radius="xs"
      className={classes.dimensionPaper}
    >
      {isEditing ? (
        <Group className={classes.editForm}>
          <Select
            label={t( 'widget_builder_dimensions_field_label', 'Field' )}
            placeholder={t( 'widget_builder_dimensions_field_placeholder', 'Select field' )}
            data={getAvailableFields()}
            value={editingValues.field}
            clearable={false}
            allowDeselect={false}
            onChange={( value ) =>
              setEditingValues({ ...editingValues, field: value || '' })
            }
            className={classes.fieldSelect}
            checkIconPosition="right"
          />
          <Button
            onClick={() => onUpdate( index, editingValues )}
            disabled={!editingValues.field}
            mt={24}
          >
            {t( 'widget_builder_dimensions_done_button', 'Done' )}
          </Button>
        </Group>
      ) : (
        <Flex justify="space-between" align="center" gap="md">
          <div {...attributes} {...listeners} className={classes.dragHandle}>
            <IconGripVertical size={16} stroke={1.5} />
          </div>
          <div className={classes.dimensionInfo}>
            <Text size="sm">
              {memberName}
            </Text>
          </div>
          <Group>
            <ActionIcon color="primary.6" variant="subtle" onClick={onEdit}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              color="danger.6"
              variant="subtle"
              onClick={onRemove}
              disabled={isRemoveDisabled}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Flex>
      )}
    </Paper>
  );
}

export default function DimensionsSection({ getAvailableFields, updateConfig, config, validationErrors }: SectionProps ) {
  const { t } = useTranslation( 'widgets' );
  const dimensions = config.dimensions || [];
  const [ showAddForm, setShowAddForm ] = useState( false );
  const [ editingIndex, setEditingIndex ] = useState<number | null>( null );
  const [ newDimension, setNewDimension ] = useState<DimensionItem>({ field: '' });

  const sectionErrors = validationErrors?.dimensions;

  const addDimension = () => {
    const newDimensions = [ ...dimensions, { ...newDimension }];
    updateConfig( 'dimensions', newDimensions );
    setNewDimension({ field: '' });
    setShowAddForm( false );
  };

  const removeDimension = ( index: number ) => {
    const newDimensions = dimensions.filter(( _, i ) => i !== index );
    updateConfig( 'dimensions', newDimensions );
    if ( editingIndex === index ) setEditingIndex( null );
  };

  const updateDimension = ( index: number, dimension: DimensionItem ) => {
    const newDimensions = [...dimensions];
    newDimensions[index] = { ...newDimensions[index], ...dimension };
    updateConfig( 'dimensions', newDimensions );
    setEditingIndex( null );
  };

  const updateNewDimension = ( field: keyof DimensionItem, value: any ) =>
    setNewDimension(( prev ) => ({ ...prev, [field]: value }));

  const sensors = useSensors(
    useSensor( PointerSensor ),
    useSensor( KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = ( event: any ) => {
    const { active, over } = event;
    if ( active.id !== over.id ) {
      const oldIndex = dimensions.findIndex(
        ( _, i ) => `dimension-${i}` === active.id
      );
      const newIndex = dimensions.findIndex(
        ( _, i ) => `dimension-${i}` === over.id
      );
      updateConfig( 'dimensions', arrayMove( dimensions, oldIndex, newIndex ));
    }
  };

  return (
    <div className={classes.container}>
      <Stack gap="md">
        <Title order={4} className={classes.sectionTitle}>
          {t( 'widget_builder_dimensions_title', 'Dimensions' )}
        </Title>

        <Alert icon={<IconInfoCircle size="1.2rem" />} color="blue.5" variant="light" mb="xs">
          <Text size="sm">
            {t(
              'widget_builder_dimensions_description',
              'Select dimensions to group your data by, such as categories, time periods, or other attributes.'
            )}
          </Text>
        </Alert>

        {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
          <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
            <Text size="sm" c="red.9">
              {t( 'widget_builder_dimensions_errors_message', 'Please fix the following errors:' )}
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {Object.entries( sectionErrors ).map(([ field, error ]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Text>
          </Alert>
        )}

        <Box className={classes.content}>
          {dimensions.length > 0 && (
            <div className={classes.dimensionsListContainer}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={dimensions.map(( _, i ) => `dimension-${i}` )}
                  strategy={verticalListSortingStrategy}
                >
                  {dimensions.map(( dimension, i ) => (
                    <SortableDimensionItem
                      key={i}
                      id={`dimension-${i}`}
                      dimension={dimension}
                      index={i}
                      isEditing={editingIndex === i}
                      onEdit={() => setEditingIndex( i )}
                      onRemove={() => removeDimension( i )}
                      onUpdate={updateDimension}
                      getAvailableFields={getAvailableFields}
                      isRemoveDisabled={false}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {showAddForm ? (
            <Paper p="md" className={classes.addForm} bg="gray.0">
              <Group className={classes.itemRow}>
                <Select
                  label={t( 'widget_builder_dimensions_field_label', 'Field' )}
                  placeholder={t( 'widget_builder_dimensions_field_placeholder', 'Select field' )}
                  data={getAvailableFields()}
                  value={newDimension.field}
                  onChange={( value ) => updateNewDimension( 'field', value )}
                  className={classes.fieldSelect}
                  checkIconPosition="right"
                />
              </Group>
              <Group justify="flex-end" mt="md">
                <Button
                  variant="outline"
                  bg="white"
                  onClick={() => setShowAddForm( false )}
                >
                  {t( 'widget_builder_dimensions_cancel_button', 'Cancel' )}
                </Button>
                <Button onClick={addDimension} disabled={!newDimension.field}>
                  {t( 'widget_builder_dimensions_add_button', 'Add' )}
                </Button>
              </Group>
            </Paper>
          ) : (
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() => setShowAddForm( true )}
              className={classes.addButton}
              fullWidth
            >
              {t( 'widget_builder_dimensions_add_dimension_button', 'Add Dimension' )}
            </Button>
          )}
        </Box>
      </Stack>
    </div>
  );
}
